"use client";

import Image, { StaticImageData } from "next/image";
import { motion } from "motion/react";
import { cn } from "@/lib/utils";
import { Section } from "./ui/section";
import { Title } from "./ui/title";
import { EASE } from "@/lib/motion";

type Props = {
  image?: StaticImageData | undefined;
  title?: string | undefined;
  subtitle?: string | undefined;
  description?: string | undefined;
  reversed?: boolean;
};

function splitLastWord(text: string): [string, string] {
  const trimmed = text.trim();
  const lastSpace = trimmed.lastIndexOf(" ");
  if (lastSpace === -1) return ["", trimmed];
  return [trimmed.slice(0, lastSpace), trimmed.slice(lastSpace + 1)];
}

export function ServiceContent({
  reversed,
  image,
  title,
  subtitle,
  description,
}: Props) {
  if (!title || !image) {
    return null;
  }

  const [head, tail] = splitLastWord(title);
  const num = reversed ? "02" : "01";

  return (
    <Section>
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.9, ease: EASE }}
            className={cn(
              "lg:col-span-7 group",
              reversed ? "lg:order-2" : "lg:order-1",
            )}
          >
            <div className="relative w-full aspect-[4/3] lg:aspect-[5/4] overflow-hidden ring-1 ring-brand-300/30">
              <motion.div
                initial={{ scale: 1.08 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 1.5, ease: EASE }}
                className="absolute inset-0 will-change-transform"
              >
                <Image
                  src={image}
                  alt=""
                  fill
                  sizes="(max-width: 1024px) 100vw, 58vw"
                  placeholder="blur"
                  className="object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.6,-0.05,0.01,0.99)] group-hover:scale-[1.025]"
                />
              </motion.div>
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-foreground/[0.04]"
              />
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.25 }}
            variants={{
              hidden: {},
              visible: {
                transition: { staggerChildren: 0.09, delayChildren: 0.1 },
              },
            }}
            className={cn(
              "lg:col-span-5 flex flex-col gap-7 lg:gap-9",
              reversed ? "lg:order-1" : "lg:order-2",
            )}
          >
            <motion.div
              variants={{
                hidden: { opacity: 0, y: 16 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: { duration: 0.7, ease: EASE },
                },
              }}
              className="flex items-center gap-4"
            >
              <span className="font-serif italic text-brand-800 text-xl leading-none">
                {num}
              </span>
              <motion.span
                variants={{
                  hidden: { scaleX: 0 },
                  visible: {
                    scaleX: 1,
                    transition: { duration: 0.85, ease: EASE },
                  },
                }}
                aria-hidden
                className="h-px flex-1 max-w-[120px] origin-left bg-brand-800/40"
              />
            </motion.div>

            <motion.div
              variants={{
                hidden: { opacity: 0, y: 24 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: { duration: 0.85, ease: EASE },
                },
              }}
            >
              <Title variant="section" accent={tail}>
                {head}
              </Title>
            </motion.div>

            {subtitle && (
              <motion.p
                variants={{
                  hidden: { opacity: 0, y: 16 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.85, ease: EASE },
                  },
                }}
                className="text-foreground/85 leading-[1.55] tracking-[-0.01em] text-base md:text-lg max-w-[560px]"
              >
                {subtitle}
              </motion.p>
            )}

            <motion.div
              variants={{
                hidden: { opacity: 0, scaleX: 0 },
                visible: {
                  opacity: 1,
                  scaleX: 1,
                  transition: { duration: 0.9, ease: EASE },
                },
              }}
              aria-hidden
              className="h-px w-14 origin-left bg-brand-800/45"
            />

            {description && (
              <motion.p
                variants={{
                  hidden: { opacity: 0, y: 16 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.85, ease: EASE },
                  },
                }}
                className="text-foreground/65 leading-[1.7] tracking-[-0.005em] text-[15px] md:text-base max-w-[560px]"
              >
                {description}
              </motion.p>
            )}
          </motion.div>
        </div>
    </Section>
  );
}
